import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'ript-autosave';
const DEBOUNCE_MS = 3000;

/**
 * useAutoSave — periodically backs up editor content to localStorage
 * and exposes recovery state on startup.
 *
 * @param {React.RefObject} editorRef - ref to the Editor imperative API
 * @param {string} fileName - current file name (stored alongside backup for display)
 * @returns {{ recovery: object|null, restoreBackup: function, discardBackup: function, clearBackup: function, triggerBackup: function }}
 */
export default function useAutoSave(editorRef, fileName) {
  // Recovery state — populated once on mount if a backup exists
  const [recovery, setRecovery] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Basic validation: must have content and timestamp
      if (parsed && parsed.content && parsed.timestamp) {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  });

  const timerRef = useRef(null);
  // Keep fileName in a ref so the debounced save always uses the latest value
  const fileNameRef = useRef(fileName);
  useEffect(() => {
    fileNameRef.current = fileName;
  }, [fileName]);

  /**
   * Write current editor content to localStorage.
   * Called by the debounce timer.
   */
  const writeBackup = useCallback(() => {
    const json = editorRef.current?.getJSON();
    if (!json) return;

    const backup = {
      content: json,
      fileName: fileNameRef.current,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(backup));
    } catch {
      // localStorage full or unavailable — silently skip
    }
  }, [editorRef]);

  /**
   * triggerBackup — call this on every content change.
   * Debounces the actual write so we don't thrash localStorage.
   */
  const triggerBackup = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      writeBackup();
      timerRef.current = null;
    }, DEBOUNCE_MS);
  }, [writeBackup]);

  /**
   * clearBackup — remove the backup from localStorage.
   * Call this after successful save, new file, or open file.
   */
  const clearBackup = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  /**
   * restoreBackup — load the backed-up content into the editor.
   * Returns the content so the caller can also set isModified, etc.
   */
  const restoreBackup = useCallback(() => {
    if (!recovery) return null;
    const { content } = recovery;
    setRecovery(null);
    clearBackup();
    return content;
  }, [recovery, clearBackup]);

  /**
   * discardBackup — user chose not to restore. Clear everything.
   */
  const discardBackup = useCallback(() => {
    setRecovery(null);
    clearBackup();
  }, [clearBackup]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return {
    recovery,
    restoreBackup,
    discardBackup,
    clearBackup,
    triggerBackup,
  };
}
